import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Wind, LogOut, Package } from "lucide-react";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  unfulfilled: "text-volt border-volt",
  shipped: "text-sky-400 border-sky-400",
  delivered: "text-green-400 border-green-400",
  cancelled: "text-red-400 border-red-400",
};

export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === null) nav("/auth");
  }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/orders/mine`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (!user) return null;

  return (
    <div className="relative min-h-screen bg-ink" data-testid="account-page">
      <header className="relative z-10 border-b border-steel">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" data-testid="account-brand">
            <Wind className="h-5 w-5 text-volt" strokeWidth={2.5} />
            <span>X8<span className="text-volt">/</span>Violent Fan</span>
          </Link>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); nav("/"); }}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-volt"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 md:py-20">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-smoke">My account</p>
        <h1 className="mt-4 font-display text-3xl font-black uppercase tracking-tight md:text-5xl">
          Hey, <span className="text-volt">{user.name?.split(" ")[0] || "fan"}</span>
        </h1>
        <p className="mt-2 font-mono text-xs text-smoke">{user.email}</p>

        <h2 className="mt-14 flex items-center gap-3 font-display text-sm font-bold uppercase tracking-wider">
          <Package className="h-4 w-4 text-volt" /> Order history
        </h2>

        {orders === null ? (
          <p className="mt-6 font-mono text-xs text-smoke">Loading…</p>
        ) : orders.length === 0 ? (
          <div className="mt-6 border border-steel bg-carbon p-10 text-center" data-testid="no-orders">
            <p className="text-smoke">No orders yet — your arsenal awaits.</p>
            <Link to="/checkout" data-testid="account-buy-cta" className="mt-6 inline-block border border-volt bg-volt px-8 py-4 font-mono text-xs font-bold tracking-[0.2em] uppercase text-ink transition-colors hover:bg-transparent hover:text-volt">
              Get the X8 — $39.99
            </Link>
          </div>
        ) : (
          <div className="mt-6 border border-steel bg-carbon" data-testid="orders-list">
            {orders.map((o, i) => (
              <div key={o.id} data-testid={`order-row-${i}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-steel px-6 py-5 last:border-b-0">
                <div>
                  <div className="font-mono text-sm font-bold text-volt">{o.id}</div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-smoke">
                    {o.bundle_label} · {new Date(o.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm">${o.amount?.toFixed(2)}</span>
                  <span className={`border px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase ${STATUS_COLORS[o.fulfillment_status] || "text-smoke border-steel"}`} data-testid={`order-status-${i}`}>
                    {o.fulfillment_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
